import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { Link, Typography } from "@mui/material";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { uniqBy } from "lodash";
import { useMemo } from "react";
import { Document, Property } from "types";
import { formatBytes } from "utils";
import { WorkspaceConfig } from "types/WorkspaceConfig";

export type useGridColumnsProps = {
    onNameClick: (name: Document) => void;
    properties: Property[];
    workspaceConfig: WorkspaceConfig;
};


const isValidNumber = (value: unknown): value is number => {
    if (value === undefined) return false;
    const isNumber = typeof value === 'number' && !isNaN(value);
    if (!isNumber) {
        console.error(`Received value (${String(value)}) of type [${typeof value}] which is not a valid number.`);
    }
    return isNumber;
}

export function useGridColumns({ onNameClick, properties, workspaceConfig }: useGridColumnsProps) {
    const gridColumns: GridColDef[] = useMemo(() => {

        const gridColumns = (properties || [])
            .filter(property => (!property.hidden))
            .map((property) => {

                // Check for invalid property type  
                if (!['string', 'number', 'date', 'dateTime', 'time', 'boolean', 'spatial'].includes(property.type)) {
                    console.warn(`Invalid property type: ${property.type}. Fallback to string.`);
                    property.type = 'string' as const;
                }
                const column: GridColDef = {
                    field: property.name,
                    headerName: property.displayName || property.name,
                    flex: 1,  // Make columns flexible by default even if no config is provided
                    editable: false,
                    type: property.type,
                    description: property.tooltip
                };

                if (property.gridColumnConfig) {
                    const { width, minWidth, maxWidth, flex } = property.gridColumnConfig;
                    if (isValidNumber(width)) {
                        column.width = width; 
                        delete column.flex;  // If width is set, remove flex because flex:1( flex: 1 1 0%) will override width
                    } else if (isValidNumber(flex)) {
                        column.flex = flex;
                    } 
                    if (isValidNumber(minWidth)) column.minWidth = minWidth;
                    if (isValidNumber(maxWidth)) column.maxWidth = maxWidth;

                }
                if (property.type === "spatial") {
                    return {
                        ...column,
                        headerName: `${property.displayName || property.name}`,
                        valueGetter: (params: GridValueGetterParams) => {
                            const val = params.row[property.name];
                            if (val && Array.isArray(val.coordinates)) {
                                return `${val.coordinates[1].toFixed(5)}, ${val.coordinates[0].toFixed(5)}`; // lat, lon
                            }
                            return "";
                        },
                        sortable: false,
                        filterable: false
                    };
                }
                if (property.type === "date" || property.type === "dateTime" || property.type === "time"){
                   column.valueFormatter =  (params) => {
                    const value = params.value;
                    if (!value) return '';

                    // Convert value to Date object if not already
                    const date = typeof value === 'string' ? new Date(value) : value;
                    if (!(date instanceof Date) || isNaN(date.getTime())) return '';

                    const formatOptions = {
                      date:     { year: 'numeric', month: '2-digit', day: '2-digit' } as const,
                      time:     { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true } as const,
                      dateTime: { year: 'numeric', month: '2-digit', day: '2-digit',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true } as const
                    };

                    let formatted = date.toLocaleString('en-NZ',
                      formatOptions[property.type] || {}
                    );

                    // Remove comma for dateTime
                    if (property.type === 'dateTime') formatted = formatted.replace(',', '');

                    return formatted;
                  }
                }
                if (property.type === "string" && property.name === "title")
                    column.renderCell = (params) => <Link maxWidth="100%" color="inherit" href="#" onClick={(e) => {
                        e.preventDefault();
                        return onNameClick && onNameClick(params.row);
                    }}>
                        <Typography overflow="hidden" textOverflow="ellipsis">{params.value}</Typography>
                    </Link>

                if (property.type === "number" && property.name === workspaceConfig.connectorConfig.fileSizeProperty){
                  column.valueFormatter = (params) =>
                        params.value == null ? '' : formatBytes(params.value, 0);
                  column.filterable = false;
                }

                // display file extension from filename instead of mime type for 'type' field   
                if (property.type === "string" && (property.name === workspaceConfig.connectorConfig.typeProperty)) {
                    const fileNameProp = workspaceConfig.connectorConfig.fileNameProperty;
                    column.valueGetter = (params: GridValueGetterParams) => {
                        const filename = params.row?.[fileNameProp];
                        const lastDotIndex = filename.lastIndexOf('.');
                        if (lastDotIndex === -1) return '';
                        return filename.substring(lastDotIndex + 1).toLowerCase();
                    };
                }
                    

                return column;
            })
            .filter(Boolean); // exclude null values  

        return uniqBy(gridColumns, x => x.field);

    }, [properties, onNameClick,]);

    return { gridColumns };
}